import { useEffect, useMemo, useState } from "react";
import { DollarSign } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { isCustomPaymentPlan, type PaymentConfiguration, type PaymentPlan } from "@/lib/api";

export interface PaymentPlanSaveData {
  paymentPlanId: number;
  paymentConfigurationIds: string[];
  comment: string;
}

interface PaymentPlanDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PaymentPlanSaveData) => Promise<void>;
  clientName: string;
  currentPaymentPlanId?: number;
  currentPaymentConfigurationIds?: string[];
  paymentPlans: PaymentPlan[];
  paymentConfigurations: PaymentConfiguration[];
  isLoading: boolean;
}

const formatPaymentStartMonth = (monthsOffset: number | null) => {
  if (monthsOffset === null) return "—";
  const currentDate = new Date();
  return new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(
    new Date(currentDate.getFullYear(), currentDate.getMonth() + monthsOffset, 1),
  );
};

const sameIds = (first: string[], second: string[]) =>
  [...first].sort().join("|") === [...second].sort().join("|");

export function PaymentPlanDialog({
  isOpen,
  onClose,
  onSave,
  clientName,
  currentPaymentPlanId,
  currentPaymentConfigurationIds = [],
  paymentPlans,
  paymentConfigurations,
  isLoading,
}: PaymentPlanDialogProps) {
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(currentPaymentPlanId ?? null);
  const [selectedConfigurationIds, setSelectedConfigurationIds] = useState<string[]>(currentPaymentConfigurationIds);
  const [comment, setComment] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedPlanId(currentPaymentPlanId ?? null);
    setSelectedConfigurationIds(currentPaymentConfigurationIds);
    setComment("");
  }, [isOpen, currentPaymentConfigurationIds, currentPaymentPlanId]);

  const sortedConfigurations = useMemo(
    () => [...paymentConfigurations].sort((first, second) =>
      (first.pagamentoDynamics ?? "").localeCompare(second.pagamentoDynamics ?? "", "pt-BR", {
        sensitivity: "base",
        numeric: true,
      })),
    [paymentConfigurations],
  );

  const selectedPlan = paymentPlans.find((plan) => plan.paymentPlanId === selectedPlanId);
  const isCustomPlanSelected = isCustomPaymentPlan(selectedPlan);
  const selectedIdsForSave = isCustomPlanSelected ? selectedConfigurationIds : [];
  const isDirty = selectedPlanId !== (currentPaymentPlanId ?? null)
    || !sameIds(selectedIdsForSave, currentPaymentConfigurationIds);
  const isValid = selectedPlanId !== null
    && (!isCustomPlanSelected || selectedConfigurationIds.length > 0)
    && comment.trim() !== "";

  const toggleConfiguration = (configurationId: string, checked: boolean) => {
    setSelectedConfigurationIds((current) => checked
      ? [...new Set([...current, configurationId])]
      : current.filter((id) => id !== configurationId));
  };

  const handleSubmit = async () => {
    if (selectedPlanId === null || !isValid) return;
    setIsSaving(true);
    try {
      await onSave({
        paymentPlanId: selectedPlanId,
        paymentConfigurationIds: selectedIdsForSave,
        comment: comment.trim(),
      });
      onClose();
    } catch {
      // O componente responsável pela persistência exibe o erro e o diálogo permanece aberto.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-hidden p-0 sm:max-w-[760px]">
        <Card className="flex max-h-[90vh] flex-col overflow-hidden border-none">
          <CardHeader className="shrink-0 bg-muted/20">
            <CardTitle className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <DollarSign className="h-4 w-4" />
              </span>
              Editar Plano de Pagamentos
            </CardTitle>
            <CardDescription>{clientName}</CardDescription>
          </CardHeader>

          <CardContent className="min-h-0 space-y-4 overflow-y-auto px-6 py-4">
            {isLoading ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Carregando planos e configurações...</p>
            ) : (
              <div className="space-y-2">
                <Label>Plano de pagamento</Label>
                <RadioGroup
                  value={selectedPlanId?.toString() ?? ""}
                  onValueChange={(value) => setSelectedPlanId(Number(value))}
                  className="gap-2"
                >
                  {paymentPlans.map((plan) => {
                    const selected = selectedPlanId === plan.paymentPlanId;
                    return (
                      <Label
                        key={plan.paymentPlanId}
                        htmlFor={`payment-plan-${plan.paymentPlanId}`}
                        className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${selected ? "border-blue-600 bg-blue-50" : "border-gray-200 bg-white hover:border-gray-300"}`}
                      >
                        <RadioGroupItem value={plan.paymentPlanId.toString()} id={`payment-plan-${plan.paymentPlanId}`} />
                        <span className="text-sm font-semibold">{plan.planName}</span>
                      </Label>
                    );
                  })}
                </RadioGroup>
              </div>
            )}

            {isCustomPlanSelected && !isLoading && (
              <div className="space-y-3 border-t border-dashed pt-4">
                <div>
                  <Label>Configurações de pagamento permitidas</Label>
                  <p className="text-xs text-muted-foreground">
                    Selecione uma ou mais condições que poderão ser usadas nos pedidos deste cliente.
                  </p>
                </div>

                <div className="overflow-hidden rounded-lg border">
                  <div className="grid grid-cols-[32px_minmax(180px,1fr)_80px_110px_minmax(150px,1fr)] gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-semibold text-muted-foreground">
                    <span />
                    <span>Condição</span>
                    <span>Parcelas</span>
                    <span>Dia</span>
                    <span>Mês de início</span>
                  </div>
                  {sortedConfigurations.map((configuration) => {
                    const configurationId = configuration.$id;
                    if (!configurationId) return null;
                    const checked = selectedConfigurationIds.includes(configurationId);
                    return (
                      <Label
                        key={configurationId}
                        htmlFor={`payment-configuration-${configurationId}`}
                        className="grid cursor-pointer grid-cols-[32px_minmax(180px,1fr)_80px_110px_minmax(150px,1fr)] items-center gap-2 border-b px-3 py-3 text-sm last:border-b-0 hover:bg-muted/30"
                      >
                        <Checkbox
                          id={`payment-configuration-${configurationId}`}
                          checked={checked}
                          onCheckedChange={(value) => toggleConfiguration(configurationId, value === true)}
                        />
                        <span className="font-medium">{configuration.pagamentoDynamics ?? "—"}</span>
                        <span>{configuration.pagamentoMagendo ?? "—"}</span>
                        <span>{configuration.diaPagamento ?? "—"}</span>
                        <span className="capitalize">{formatPaymentStartMonth(configuration.meses)}</span>
                      </Label>
                    );
                  })}
                </div>

                {sortedConfigurations.length === 0 && (
                  <p className="text-sm text-destructive">Nenhuma configuração de pagamento está disponível.</p>
                )}
                <p className="text-xs font-medium text-muted-foreground">
                  {selectedConfigurationIds.length} configuraç{selectedConfigurationIds.length === 1 ? "ão selecionada" : "ões selecionadas"}
                </p>
              </div>
            )}

            <div className="space-y-2 border-t border-dashed pt-4">
              <Label htmlFor="payment-plan-comment">Comentário:</Label>
              <Textarea
                id="payment-plan-comment"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Digite o motivo da alteração..."
                className="min-h-[80px]"
              />
            </div>
          </CardContent>

          <CardFooter className="flex shrink-0 justify-end gap-2 border-t bg-muted/20 p-6">
            <Button variant="ghost" onClick={onClose} disabled={isSaving}>Cancelar</Button>
            <Button variant="success" onClick={handleSubmit} disabled={isSaving || !isValid || !isDirty}>
              {isSaving ? "Salvando..." : "Salvar alterações"}
            </Button>
          </CardFooter>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
